import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  Typography,
  Divider,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../context/AuthContext';
import { navigationConfig } from '../../config/navigation';

const Sidebar = ({ drawerWidth, mobileOpen, onClose, isDesktop }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasFeature, logout } = useAuth();
  const { t } = useTranslation('layout');
  const labelOf = (item) => (item.labelKey ? t(item.labelKey) : item.label);
  const [openGroups, setOpenGroups] = useState({});

  const handleGroupToggle = (groupId) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (!isDesktop) {
      onClose();
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isGroupActive = (group) => {
    if (group.path) return isActive(group.path);
    if (group.children) {
      return group.children.some((child) => isActive(child.path));
    }
    return false;
  };

  const hasVisibleChildren = (group) => {
    if (!group.children) return true;
    return group.children.some((child) => !child.feature || hasFeature(child.feature));
  };

  const sidebarStyles = {
    bgcolor: '#0f0a2a',
    color: 'rgba(255,255,255,0.85)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };

  const activeItemStyles = {
    bgcolor: 'rgba(99, 91, 255, 0.9)',
    color: 'white',
    borderRadius: 2,
    '&:hover': { bgcolor: 'rgba(99, 91, 255, 1)' },
    '& .MuiListItemIcon-root': { color: 'white' },
  };

  const inactiveItemStyles = {
    borderRadius: 2,
    color: 'rgba(255,255,255,0.7)',
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.08)',
      color: 'white',
    },
    '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.5)' },
    '&:hover .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.9)' },
  };

  const drawerContent = (
    <Box sx={sidebarStyles}>
      <Toolbar sx={{ px: 2.5, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/evi-logo.jpg"
            alt="EVI"
            sx={{ height: 36, width: 36, borderRadius: 1 }}
          />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.5 }}>
            EVI
          </Typography>
        </Box>
      </Toolbar>

      <List component="nav" sx={{ px: 1.5, py: 1, flex: 1, overflow: 'auto' }}>
        {navigationConfig.map((item) => {
          if (!hasVisibleChildren(item)) return null;

          if (!item.children) {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <ListItemButton
                key={item.id}
                onClick={() => handleNavClick(item.path)}
                sx={{
                  mb: 0.5,
                  py: 1,
                  px: 2,
                  ...(active ? activeItemStyles : inactiveItemStyles),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={labelOf(item)}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: active ? 600 : 500,
                  }}
                />
              </ListItemButton>
            );
          }

          const Icon = item.icon;
          const groupActive = isGroupActive(item);
          const isOpen = openGroups[item.id] ?? groupActive;

          return (
            <React.Fragment key={item.id}>
              <ListItemButton
                onClick={() => handleGroupToggle(item.id)}
                sx={{
                  mb: 0.5,
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  color: groupActive ? 'white' : 'rgba(255,255,255,0.7)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: 'white' },
                  '& .MuiListItemIcon-root': {
                    color: groupActive ? 'rgba(99, 91, 255, 1)' : 'rgba(255,255,255,0.5)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Icon sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={labelOf(item)}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: groupActive ? 600 : 500,
                  }}
                />
                {isOpen ? (
                  <ExpandLess sx={{ fontSize: 18, opacity: 0.7 }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 18, opacity: 0.7 }} />
                )}
              </ListItemButton>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 1 }}>
                  {item.children.map((child) => {
                    if (child.feature && !hasFeature(child.feature)) return null;
                    const ChildIcon = child.icon;
                    const childActive = isActive(child.path);
                    return (
                      <ListItemButton
                        key={child.id}
                        onClick={() => handleNavClick(child.path)}
                        sx={{
                          py: 0.75,
                          pl: 4,
                          pr: 2,
                          mb: 0.25,
                          ...(childActive ? activeItemStyles : inactiveItemStyles),
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <ChildIcon sx={{ fontSize: 18 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={labelOf(child)}
                          primaryTypographyProps={{
                            fontSize: '0.8125rem',
                            fontWeight: childActive ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>

      {/* Bottom section: Help + Logout */}
      <Box sx={{ px: 1.5, pb: 2 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 1 }} />
        <List disablePadding>
          <ListItemButton
            sx={{ ...inactiveItemStyles, py: 1, px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <HelpOutlineIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={t('help')}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
            />
          </ListItemButton>
          <ListItemButton
            onClick={logout}
            sx={{ ...inactiveItemStyles, py: 1, px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LogoutIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={t('logout')}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
            />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: '#0f0a2a',
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: '#0f0a2a',
            border: 'none',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
